import { titleCase } from '@/utils'
import {
  deriveAffectedDocsFromPublishedDocsData,
  docsDataFromPublishedDocs,
} from '../../publish/publishDiff'
import { ACTIVITY_ACTIONS, getOperationGroupMeta } from './constants'

/** 與 EditDoc Switch 一致：陣列（含 []）= ON，null/undefined = OFF */
export const isIpWhiteListOn = value => Array.isArray(value)

/**
 * allowedIPs 開關變更時顯示 ON / OFF；否則回 null（走一般欄位格式）
 */
export const formatIpWhiteListSwitchChange = (oldValue, newValue) => {
  const oldOn = isIpWhiteListOn(oldValue)
  const newOn = isIpWhiteListOn(newValue)
  if (oldOn === newOn) return null
  return {
    oldText: oldOn ? 'ON' : 'OFF',
    newText: newOn ? 'ON' : 'OFF',
  }
}

const normalizeAllowedIpItem = (item) => {
  if (item == null) return null
  if (typeof item === 'string' || typeof item === 'number') {
    const ip = String(item).trim()
    return ip ? { ip, remark: '' } : null
  }
  const ip = String(item.ip || '').trim()
  if (!ip) return null
  return { ip, remark: String(item.remark || '') }
}

const allowedIpListToMap = (list) => {
  const map = new Map()
  if (!Array.isArray(list)) return map
  list.forEach((item) => {
    const normalized = normalizeAllowedIpItem(item)
    if (!normalized) return
    map.set(normalized.ip, normalized)
  })
  return map
}

/**
 * 以 ip 為列識別，比對白名單增／刪／改（不需 row id）
 * 改 IP 本身會是刪舊 + 增新
 */
export const diffAllowedIpRows = (oldValue, newValue) => {
  const oldMap = allowedIpListToMap(oldValue)
  const newMap = allowedIpListToMap(newValue)
  const rows = []

  oldMap.forEach((oldItem, ip) => {
    const newItem = newMap.get(ip)
    if (!newItem) {
      rows.push({
        mode: 'delete',
        actionLabel: '刪除',
        ip,
        oldRemark: oldItem.remark,
        newRemark: '',
      })
      return
    }
    if (oldItem.remark !== newItem.remark) {
      rows.push({
        mode: 'update',
        actionLabel: '修改',
        ip,
        oldRemark: oldItem.remark,
        newRemark: newItem.remark,
      })
    }
  })

  newMap.forEach((newItem, ip) => {
    if (oldMap.has(ip)) return
    rows.push({
      mode: 'create',
      actionLabel: '新增',
      ip,
      oldRemark: '',
      newRemark: newItem.remark,
    })
  })

  return rows
}

/**
 * 合併 docsDataByDocId 與 activity 快取的 docsMeta
 * 優先使用 docsDataByDocId[docId].docShortTitle
 */
export const resolveDocsMeta = (docsDataByDocId = {}, cachedDocsMeta = {}, docIds = []) => {
  const ids = new Set([
    ...docIds,
    ...Object.keys(cachedDocsMeta || {}),
  ])
  const result = {}
  ids.forEach((docId) => {
    if (!docId) return
    const liveTitle = docsDataByDocId?.[docId]?.docShortTitle
    const cachedTitle = cachedDocsMeta?.[docId]?.docShortTitle
    result[docId] = {
      ...(cachedDocsMeta?.[docId] || {}),
      docShortTitle: liveTitle || cachedTitle || docId,
    }
  })
  return result
}

/**
 * 從母本 before/after（docs／excludedDocs 與是否有內容類變更）推算影響子本
 */
export const deriveAffectedDocs = ({
  beforeDocs = [],
  afterDocs = [],
  beforeExcludedDocs = [],
  afterExcludedDocs = [],
  contentChanged = false,
  docsMeta = {},
} = {}) => {
  const beforeSet = new Set(beforeDocs)
  const afterSet = new Set(afterDocs)
  const beforeExcludedSet = new Set(beforeExcludedDocs)
  const result = []
  const seen = new Set()

  const shortTitle = docId => docsMeta[docId]?.docShortTitle || docId
  const pushUnique = (item) => {
    if (seen.has(item.docId)) return
    seen.add(item.docId)
    result.push(item)
  }

  afterDocs.forEach((docId) => {
    if (!beforeSet.has(docId)) {
      pushUnique({
        docId,
        docShortTitle: shortTitle(docId),
        effect: 'added',
        effectLabel: '將新增套用此區塊／參數',
      })
    } else if (contentChanged) {
      pushUnique({
        docId,
        docShortTitle: shortTitle(docId),
        effect: 'updated',
        effectLabel: '參數說明將更新',
      })
    }
  })

  beforeDocs.forEach((docId) => {
    if (!afterSet.has(docId)) {
      pushUnique({
        docId,
        docShortTitle: shortTitle(docId),
        effect: 'removed',
        effectLabel: '將不再套用此區塊／參數',
      })
    }
  })

  afterExcludedDocs.forEach((docId) => {
    if (!beforeExcludedSet.has(docId)) {
      pushUnique({
        docId,
        docShortTitle: shortTitle(docId),
        effect: 'excluded',
        effectLabel: '此參數將設為 Not Used In（該子本不再顯示）',
      })
    }
  })

  // 有 content 變更但未帶 docs diff 時，用 docsMeta 推算其餘子本
  if (contentChanged && !beforeDocs.length && !afterDocs.length) {
    Object.keys(docsMeta).forEach((docId) => {
      if (afterExcludedDocs.includes(docId) && !beforeExcludedSet.has(docId)) return
      pushUnique({
        docId,
        docShortTitle: shortTitle(docId),
        effect: 'updated',
        effectLabel: '參數說明將更新',
      })
    })
  }

  return result
}

/** 從 publish detail.blocks 抽出 docs／excludedDocs before/after 與是否有 content 變更 */
export const extractPublishImpactHints = (detail = {}) => {
  const blocks = detail.blocks || []
  let beforeDocs
  let afterDocs
  let beforeExcludedDocs
  let afterExcludedDocs
  let contentChanged = false

  blocks.forEach((block) => {
    (block.diffs || []).forEach((diff) => {
      if (diff.key === 'docs') {
        beforeDocs = Array.isArray(diff.oldValue) ? diff.oldValue : []
        afterDocs = Array.isArray(diff.newValue) ? diff.newValue : []
      }
      if (diff.key === 'excludedDocs') {
        beforeExcludedDocs = Array.isArray(diff.oldValue) ? diff.oldValue : []
        afterExcludedDocs = Array.isArray(diff.newValue) ? diff.newValue : []
      }
      if (diff.key === 'content' || (diff.path || '').includes('content')) {
        contentChanged = true
      }
    })
  })

  return {
    beforeDocs: beforeDocs || [],
    afterDocs: afterDocs || [],
    beforeExcludedDocs: beforeExcludedDocs || [],
    afterExcludedDocs: afterExcludedDocs || [],
    contentChanged,
    docsMeta: detail.docsMeta || {},
  }
}

export const isActivityLogPublish = entry => (
  entry?.action === ACTIVITY_ACTIONS.PUBLISH
  || ['publish', 'publish_master'].includes(entry?.detail?.kind)
)

/** Delete Platform 觸發的發佈：Drawer 顯示 Delete Platform: ACEWIN */
export const getActivityLogPublishCausedByLabel = (causedBy) => {
  if (!causedBy || causedBy.type !== ACTIVITY_ACTIONS.PLATFORM_DELETE) return ''
  const name = causedBy.entityName || causedBy.entityId
  return name ? `Delete Platform: ${name}` : 'Delete Platform'
}

export const getActivityLogDrawerTitle = (entry) => {
  const op = getOperationGroupMeta(entry?.action).label
  const causedByLabel = getActivityLogPublishCausedByLabel(entry?.detail?.causedBy)
  console.log('getActivityLogDrawerTitle entry?.detail', entry?.detail, entry?.detail?.causedBy, causedByLabel)
  if (causedByLabel) return `${op} - ${causedByLabel}`
  if (entry?.wallet) return `${op} - ${titleCase(entry.wallet)} Wallet`
  return op
}

export const isActivityLogChildDocTarget = (entry) => {
  if (Array.isArray(entry?.target?.docIds) && entry.target.docIds.length) return false
  const docId = entry?.target?.docId
  return !!(docId && !String(docId).includes('master'))
}

export const getActivityLogMasterDocIds = (entry) => {
  const docIds = entry?.target?.docIds
  if (Array.isArray(docIds) && docIds.length) return docIds.filter(Boolean)
  const docId = entry?.target?.docId
  if (docId && String(docId).includes('master')) return [docId]
  return []
}

const walletLabelFromDocId = (docId) => {
  const raw = String(docId || '')
  const wallet = raw.includes('master') ? raw.split('#')[0] : raw.split('#')[1]
  return wallet ? `${titleCase(wallet)} Wallet` : ''
}

export const getActivityLogWalletLabel = entry => (
  entry?.wallet ? `${titleCase(entry.wallet)} Wallet` : '—'
)

const resolvePublishedDocTitle = (entry, docId, docsDataByDocId = {}) => {
  const published = entry?.detail?.publishedDocsDataByDocId?.[docId]
  return published?.docShortTitle
    || published?.docData?.docShortTitle
    || docsDataByDocId?.[docId]?.docShortTitle
    || entry?.target?.docShortTitle
    || entry?.target?.entityName
    || docId
    || '—'
}

export const getActivityLogAffectedDocs = (entry, docsDataByDocId = {}) => {
  if (!isActivityLogPublish(entry) || isActivityLogChildDocTarget(entry)) return []
  const published = entry?.detail?.publishedDocsDataByDocId
  if (!published) return []
  return deriveAffectedDocsFromPublishedDocsData(
    published,
    docsDataFromPublishedDocs(published, docsDataByDocId),
  )
}

/** Target 欄顯示：母本操作加 [母本]；publish 另列 [影響子本] */
export const getActivityLogTargetModel = (entry, docsDataByDocId = {}) => {
  if (isActivityLogChildDocTarget(entry)) {
    const { docId } = entry.target
    const primary = resolvePublishedDocTitle(entry, docId, docsDataByDocId)
    return {
      showMaster: false,
      masterLabels: [],
      masterLabel: '',
      primary,
      affectedDocs: [],
    }
  }

  const masterDocIds = getActivityLogMasterDocIds(entry)
  const masterLabels = [...new Set(
    (masterDocIds.length
      ? masterDocIds.map((docId) => {
        const title = resolvePublishedDocTitle(entry, docId, docsDataByDocId)
        if (title && title !== '—' && title !== docId) return title
        return walletLabelFromDocId(docId)
      })
      : [getActivityLogWalletLabel(entry)]
    ).filter(label => label && label !== '—'),
  )]
  const showMaster = masterLabels.length > 0
  return {
    showMaster,
    masterLabels,
    masterLabel: masterLabels[0] || '',
    primary: masterLabels[0] || '—',
    affectedDocs: isActivityLogPublish(entry)
      ? getActivityLogAffectedDocs(entry, docsDataByDocId)
      : [],
  }
}

export const getActivityLogTargetFilterValues = (entry, docsDataByDocId = {}) => {
  const model = getActivityLogTargetModel(entry, docsDataByDocId)
  const values = [...(model.masterLabels || [])]
  if (!values.length && model.primary && model.primary !== '—') values.push(model.primary)
  model.affectedDocs.forEach((doc) => {
    if (doc?.docShortTitle) values.push(doc.docShortTitle)
  })
  return [...new Set(values)]
}

export const activityLogMatchesTargetFilter = (entry, selected, docsDataByDocId = {}) => {
  if (!selected) return true
  const model = getActivityLogTargetModel(entry, docsDataByDocId)
  if (selected === '—') {
    return !model.masterLabels?.length && (!model.primary || model.primary === '—') && !model.affectedDocs.length
  }
  if (model.masterLabels?.includes(selected)) return true
  if (model.primary === selected) return true
  return model.affectedDocs.some(doc => doc?.docShortTitle === selected)
}
